import * as Yup from "yup";

import {
  fieldConfigurations,
  FieldConfiguration,
  PIC_FIELD_INDICES,
  NOTES_FIELD_INDICES,
} from "./fieldConfigurations";
import { buildSideLoadedValidationSchema } from "./validationSchema";
import {
  parsePicValues,
  PIC_DISPLAY_DELIMITER,
} from "constants/sideLoadedBusinesses";

export function getInitialAutoMappedValues(
  rawData: Record<string, string | number> = {},
  configurations: FieldConfiguration[] = Object.values(fieldConfigurations),
): Record<string, string | number | boolean | null> {
  const mappableFields = Object.keys(rawData || {});
  return configurations.reduce((acc, configuration) => {
    acc[`MappableField__${configuration.value}`] = null;
    acc[`Value__${configuration.value}`] = "";

    mappableFields.forEach(sourceFieldName => {
      const simplifiedSourceName = sourceFieldName
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
      if (
        configuration.sourceFieldSuggestions.includes(simplifiedSourceName) &&
        rawData[sourceFieldName] !== undefined &&
        rawData[sourceFieldName] !== null &&
        rawData[sourceFieldName] !== ""
      ) {
        if (configuration.handleAsNotes || configuration.handleAsProperty) {
          acc[`MappableField__${configuration.value}`] = true;
          const fieldIndices = configuration.handleAsProperty
            ? PIC_FIELD_INDICES
            : NOTES_FIELD_INDICES;
          const firstEmptyIndex = fieldIndices.find(
            index => !acc[`MappableField__${configuration.value}__${index}`],
          );
          if (firstEmptyIndex !== undefined) {
            acc[`MappableField__${configuration.value}__${firstEmptyIndex}`] =
              sourceFieldName;
          }
        } else {
          acc[`MappableField__${configuration.value}`] = sourceFieldName;
          acc[`Value__${configuration.value}`] = rawData[sourceFieldName];
        }
      }
    });

    if (configuration.handleAsProperty) {
      const picFieldKeys = PIC_FIELD_INDICES.map(
        index => `MappableField__${configuration.value}__${index}`,
      );
      const columnNames = picFieldKeys.map(k => acc[k]);
      if (columnNames.some(Boolean)) {
        acc[`Value__${configuration.value}`] = parsePicValues(
          columnNames.map(colName =>
            colName ? rawData[colName]?.toString() : undefined,
          ),
        ).join(PIC_DISPLAY_DELIMITER);
      }
    } else if (configuration.handleAsNotes) {
      const noteFieldKeys = NOTES_FIELD_INDICES.map(
        index => `MappableField__${configuration.value}__${index}`,
      );
      const noteColumnNames = noteFieldKeys.map(k => acc[k]);
      if (noteColumnNames.some(Boolean)) {
        acc[`Value__${configuration.value}`] = noteColumnNames
          .filter(fieldName => fieldName && rawData[fieldName] !== undefined)
          .map(fieldName => `${fieldName}: ${rawData[fieldName]}`)
          .join("\n");
      }
    }

    return acc;
  }, {});
}

export interface SideLoadedValidationOptions {
  businessIdByAgentCodeLookup?: Record<string, string>;
  businessIdBySaleyardCodeLookup?: Record<string, string>;
  configurations?: FieldConfiguration[];
}

export function getSideLoadedBusinessValidationIssues(
  record: SideLoadedBusiness,
  {
    businessIdByAgentCodeLookup = {},
    businessIdBySaleyardCodeLookup = {},
    configurations = Object.values(fieldConfigurations),
  }: SideLoadedValidationOptions = {},
): string[] {
  const rawData = record?.rawData || {};
  const values = getInitialAutoMappedValues(rawData, configurations);
  const schema = buildSideLoadedValidationSchema(configurations, {
    rawData,
    businessIdByAgentCodeLookup,
    businessIdBySaleyardCodeLookup,
  });

  try {
    schema.validateSync(values, { abortEarly: false });
    return [];
  } catch (error) {
    if (error instanceof Yup.ValidationError) {
      return error.errors;
    }
    throw error;
  }
}
